import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active disabled:bg-primary-disabled disabled:opacity-100',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive-hover',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-secondary bg-card text-accent-foreground hover:bg-primary-subtle active:bg-primary-soft',
        ghost: 'hover:bg-primary-subtle hover:text-accent-foreground active:bg-primary-soft',
        link: 'text-primary underline-offset-4 hover:underline',
        // Form alanı gibi görünen tetikleyici (çoklu seçim, tarih/saat seçici): Input ve Select ile aynı zemin ve kenarlık.
        field:
          'border border-input bg-input-background font-normal text-foreground hover:border-input-hover hover:bg-input-background focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-input-focus-ring focus-visible:ring-offset-0',
        // Aç/kapa seçim çipi (ör. grup üyesi yetkileri): seçili durum aria-pressed ile verilir.
        // Kilitli (disabled) çipte tooltip gösterilebilsin diye pointer olayları açık kalır.
        chip: 'border border-input bg-card text-muted-foreground hover:bg-muted aria-pressed:border-primary-soft aria-pressed:bg-primary-subtle aria-pressed:text-primary aria-pressed:hover:bg-primary-soft disabled:pointer-events-auto disabled:cursor-not-allowed',
        // Herkese açık pazarlama sayfaları (anasayfa, modül sayfaları; mkt-* token'ları)
        mktInk: 'bg-mkt-ink text-white hover:bg-mkt-ink-hover active:bg-mkt-ink',
        mktOutline: 'border border-mkt-ink/25 bg-transparent text-mkt-ink hover:bg-mkt-ink/5',
        mktIcon: 'border border-mkt-ink/12 bg-transparent text-mkt-ink shadow-none hover:bg-mkt-ink/5',
        mktLight: 'bg-mkt-cta-heading text-mkt-ink hover:bg-white',
        mktLightOutline:
          'border border-mkt-cta-heading/35 bg-transparent text-mkt-cta-heading hover:bg-mkt-cta-heading/10',
      },
      size: {
        default: 'h-12 px-6 py-3',
        sm: 'h-10 rounded-lg px-4 text-xs',
        lg: 'h-14 rounded-xl px-8 text-base',
        icon: 'h-12 w-12',
        chip: 'h-auto gap-1.5 rounded-full px-3 py-1.5 text-xs',
        mkt: 'h-[50px] gap-2.5 rounded-[12px] px-6 font-mkt-sans text-[15px] font-semibold',
        mktCompact: 'h-12 rounded-[10px] px-[22px] font-mkt-sans text-[15px] font-medium',
        mktNav: 'h-[46px] gap-2.5 rounded-[12px] px-[22px] font-mkt-sans text-[15px] font-semibold',
        mktNavSm: 'h-[42px] gap-2 rounded-[12px] px-4 font-mkt-sans text-[14px] font-semibold',
        mktIcon: 'h-[42px] w-[42px] rounded-[12px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} disabled={disabled} {...props} />
    );
  }
);
Button.displayName = 'Button';
export { Button, buttonVariants };
